import { jsx } from 'panel';
import { SelectEl } from 'common/components/select';
import { ModalEl } from 'common/components/modal';
import { DropMenuEl } from 'common/components/drop-menu';
import { ItemsMenuEl } from 'common/components/items-menu';
import { SpinnerEl } from 'common/components/spinner';
import { TextHighlightEl } from 'common/components/text-highlight';
import { TooltipEl } from 'common/components/tooltip';
import { TagInputEl } from 'common/components/tag-input';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { ShareOptionsLocation, SharingModalViewType } from 'common/widgets/sharing/modal/types';
import { pluralize, truncate } from 'common/util/string';
import { BannerEl } from 'common/components/banner';
import { BannerAction, BannerTheme, BannerType } from 'common/components/banner/types';
import { ButtonEl } from 'common/components/button';
import { SwitchEl } from 'common/components/switch';
import { TableEl } from 'common/components/table';
import { LoadingStatus } from 'common/widgets/sharing/types';
import { LinkDetailMixin } from 'common/templates/public-dashboards/mixins';
import { Placement } from 'common/components/tooltip/types';
import { currentProjectStore } from 'common/report/stores/project-details';
import { SvgIconEl } from 'common/components/svg-icon';
import TableStyle from './public-links-table.styl?inline';
import { ShareSection } from './helpers';

const PublicDashboardDetailsView = _ref => {
  let {
    state,
    helpers,
    showSuperadminBanner
  } = _ref;
  return jsx("div", {
    sel: '.wrapper',
    class: {
      'no-min-height': helpers.shouldShowUpsellBanner()
    }
  }, jsx("header", {
    sel: '.modal-header'
  }, "Public Dashboard Details"), jsx("div", {
    sel: '.content'
  }, showSuperadminBanner ? jsx(BannerEl, {
    style: {
      'margin-bottom': "20px"
    },
    attrs: {
      'body-text': "You have not been granted access to edit this Dashboard, but as a Project Owner you can make changes.",
      theme: BannerTheme.Info,
      type: BannerType.Embedded
    }
  }) : null, jsx("p", {
    sel: '.public-dashboard-disclaimer'
  }, "Non Project members can access a ", jsx("strong", null, "read-only"), " instance of a Public Dashboard with a link. ", jsx("br", null), "Learn more about Public Dashboards on our Help docs."), state.publicDashboardLinks.loading === LoadingStatus.Success ? jsx("div", {
    sel: '.banner-wrapper'
  }, jsx("div", {
    sel: '.color-indicator'
  }), jsx("p", null, "This Dashboard ", jsx("b", null, "\"", helpers.getEntityName(), "\""), " has ", state.publicDashboardLinks.data.length, " active Public ", pluralize("link", state.publicDashboardLinks.data.length, "links"))) : null, jsx(TableEl, {
    sel: '.mp-table',
    attrs: {
      elref: "publicDashboardLinksTable",
      'empty-message': "No public dashboard links",
      'style-override': TableStyle,
      'track-name': "Public Dashboard Links",
      'window-scroll': true,
      'max-body-height': "450px",
      'disable-clickable-rows': true,
      'disable-expandable-rows': true,
      loading: state.publicDashboardLinks.loading === LoadingStatus.Pending
    },
    props: {
      rows: helpers.publicDashboard.getPublicDashboardLinksTableRows(),
      columns: helpers.publicDashboard.getPublicDashboardLinksTableCols(),
      rowDetailsRenderer: LinkDetailMixin(helpers.publicDashboard.handlePasswordChange)
    },
    on: {
      rowDetailsVisibilityChange: helpers.publicDashboard.handleRowDetailsVisibilityChange
    }
  })), jsx("footer", {
    sel: '.modal-actions'
  }, jsx(ButtonEl, {
    sel: '.public-dashboard-back-button',
    attrs: {
      elref: "publicDashboardDetailsBackButton",
      theme: ButtonTheme.Secondary
    },
    on: {
      click: () => helpers.publicDashboard.updateSharingModalViewType(SharingModalViewType.Sharing)
    }
  }, "Back"), jsx(ButtonEl, {
    sel: '.public-dashboard-done-button',
    attrs: {
      theme: ButtonTheme.Primary
    },
    on: {
      click: helpers.publicDashboard.handlePublicDashboardDoneClick
    }
  }, "Done")));
}; // eslint-disable-next-line sonarjs/cognitive-complexity


export function template() {
  const {
    pendingCanEdit,
    pendingGrants,
    searchText,
    showCopyLinkText,
    showPopover,
    publicDashboardEnabled,
    modalViewType
  } = this.state;
  const projectMemberCount = this.helpers.getProjectMemberCount();
  const showDisabledBanner = this.attr("disabled") && this.attr("disabled-reason") && !this.helpers.shouldShowUpsellBanner();
  const showSuperadminBanner = this.attr("is-superadmin") && !this.helpers.shouldShowUpsellBanner();
  return jsx(ModalEl, {
    attrs: {
      closeable: true,
      'modal-title': "Share this ".concat(this.helpers.getEntityTypeTitle(), " With Others"),
      'modal-description': "Modal for managing sharing permissions",
      open: this.attr("open")
    }
  }, modalViewType === SharingModalViewType.PublicDashboardDetails ? jsx(PublicDashboardDetailsView, {
    state: this.state,
    helpers: this.helpers,
    showSuperadminBanner: showSuperadminBanner
  }) : jsx("div", {
    sel: ".wrapper",
    class: {
      'no-min-height': this.helpers.shouldShowUpsellBanner(),
      'with-banner': showDisabledBanner || showSuperadminBanner
    }
  }, jsx("header", {
    sel: '.modal-header'
  }, "Share this ", this.helpers.getEntityTypeTitle()), jsx("div", {
    sel: '.content'
  }, showDisabledBanner ? jsx(BannerEl, {
    style: {
      'margin-bottom': "20px"
    },
    attrs: {
      'body-text': this.attr("disabled-reason"),
      theme: BannerTheme.Info,
      type: BannerType.Embedded
    }
  }) : null, showSuperadminBanner ? jsx(BannerEl, {
    style: {
      'margin-bottom': "20px"
    },
    attrs: {
      'body-text': "You have not been granted access to edit this ".concat(this.helpers.getEntityTypeTitle(), ", but as a Project Owner you can make changes."),
      theme: BannerTheme.Info,
      type: BannerType.Embedded
    }
  }) : null, this.helpers.showExistingSharesSection() ? jsx(null, null, this.helpers.shouldShowUpsellBanner() ? jsx(null, null, jsx(BannerEl, {
    attrs: {
      elref: "upsellBanner",
      theme: BannerTheme.Info,
      type: BannerType.Embedded,
      'body-text': "On your current plan, ".concat(currentProjectStore.value.eventsPlanName, ", everything you create is visible and editable by others in the project. Upgrade your plan to gain access to advanced sharing and additional privacy controls."),
      ctas: [{
        text: "Upgrade",
        actionName: BannerAction.Upgrade,
        icon: "share",
        theme: ButtonTheme.Tertiary,
        href: "/pricing"
      }]
    }
  }), jsx("div", {
    sel: '.existing-shares',
    attrs: {
      elref: "existingShares"
    },
    key: 'existing-shares',
    hook: {
      insert: _ref2 => {
        let {
          elm
        } = _ref2;
        return this.helpers.handleShareSectionInsert(elm);
      }
    },
    class: {
      'viewer-state': this.attr("disabled")
    }
  }, ShareSection("People", null, this.helpers, true))) : jsx(null, null, jsx("div", {
    sel: '.picker-wrapper',
    key: 'wrapper',
    hook: {
      insert: _ref3 => {
        let {
          elm
        } = _ref3;
        return this.setElmRef("tagInputWrapper", elm);
      }
    }
  }, jsx("label", {
    attrs: {
      for: "picker"
    }
  }, "Invite Teammates"), jsx("div", {
    sel: '.picker-controls'
  }, jsx(TagInputEl, {
    attrs: {
      id: "picker",
      tags: this.helpers.picker.getTags(),
      elref: "sharingTagInput",
      placeholder: "Enter People or Team Names...",
      'min-tags': 0,
      'disable-input': this.attr("disabled"),
      'mp-select-attrs': {
        'disable-search': true,
        sections: this.helpers.getSelectOptions(ShareOptionsLocation.PickerControls),
        'menu-width': 120,
        'selected-option': pendingCanEdit ? "Editor" : "Viewer",
        theme: "tertiary",
        'always-show-caret': true,
        elref: "selectShareLevel",
        disabled: this.attr("disabled")
      }
    },
    on: {
      change: this.helpers.picker.handleInputChange,
      focus: this.helpers.picker.handleInputFocus,
      optionSelected: this.helpers.picker.handleLevelSelectChange
    },
    hook: {
      insert: this.helpers.picker.handleMpTagInputInsert
    }
  }), jsx(DropMenuEl, {
    attrs: {
      open: !!searchText && showPopover,
      'prevent-close-selectors': JSON.stringify(["mp-tag-input"])
    },
    hook: {
      insert: _ref4 => {
        let {
          elm
        } = _ref4;
        return this.setElmRef("dropMenu", elm);
      }
    },
    on: {
      change: this.helpers.picker.handlePopoverChange
    }
  }, jsx(ItemsMenuEl, {
    attrs: {
      'search-filter': searchText,
      'empty-message': "This list is empty",
      elref: "usersAndTeamsMenu"
    },
    props: {
      sections: this.helpers.picker.getMenuSections(),
      keydownProxyEl: this.elmRefs.tagInput
    },
    on: {
      clickItem: this.helpers.picker.handleItemClick
    }
  })), jsx(ButtonEl, {
    sel: '.save-share',
    attrs: {
      disabled: pendingGrants.size === 0,
      elref: "saveNewSharesButton",
      size: ButtonSize.Large,
      theme: ButtonTheme.Primary
    },
    on: {
      click: this.helpers.picker.handleShareButtonClick
    }
  }, "Share"))), jsx("div", {
    sel: '.existing-shares',
    attrs: {
      elref: "existingShares"
    },
    key: 'existing-shares',
    hook: {
      insert: _ref5 => {
        let {
          elm
        } = _ref5;
        return this.helpers.handleShareSectionInsert(elm);
      }
    },
    class: {
      'viewer-state': this.attr("disabled")
    }
  }, ShareSection("People", this.helpers.existingShares.getUserGrants(), this.helpers, this.attr("disabled")), ShareSection("Teams", this.helpers.existingShares.getTeamGrants(), this.helpers, this.attr("disabled"))))) : jsx("div", {
    sel: '.placeholder'
  }, jsx("span", null, "Loading current shares..."), jsx(SpinnerEl, null))), this.helpers.showProjectShareSection() ? jsx("div", {
    sel: '.share-section--project',
    attrs: {
      elref: "shareSectionProject"
    }
  }, jsx("header", null, this.helpers.publicDashboard.showPublicDashboardSwitch() ? "Advanced" : "Project"), jsx("ul", {
    sel: '.share-list'
  }, jsx("li", {
    sel: '.project-share'
  }, jsx("div", {
    sel: '.display'
  }, jsx("div", {
    sel: '.name-wrapper'
  }, jsx("div", {
    sel: '.project-name'
  }, this.helpers.getProjectShareLabel()), !this.helpers.shouldShowUpsellBanner() && jsx(TextHighlightEl, {
    attrs: {
      theme: "success",
      text: this.helpers.getProjectShareGrantStatus()
    }
  })), this.attr("disabled") || this.helpers.publicDashboard.showPublicDashboardSwitch() ? null : jsx("div", {
    sel: '.subtitle'
  }, "".concat(projectMemberCount, " ").concat(pluralize("person", projectMemberCount, "people")))), jsx("div", {
    sel: '.controls'
  }, jsx(SelectEl, {
    sel: '.share-level',
    attrs: {
      'disable-search': true,
      'menu-width': 120,
      theme: "tertiary",
      'always-show-caret': true,
      sections: JSON.stringify(this.helpers.getSelectOptions(ShareOptionsLocation.ProjectShare)),
      'selected-option': this.helpers.getProjectShareSelectOption(),
      elref: "selectShareLevel_".concat(this.attr("project-id")),
      disabled: this.attr("disabled")
    },
    on: {
      change: this.helpers.handleProjectShareSelectChange
    }
  }))), this.helpers.publicDashboard.showPublicDashboardSwitch() ? jsx("li", {
    sel: '.project-share'
  }, jsx("div", {
    sel: '.display'
  }, jsx("div", {
    sel: '.name-wrapper',
    class: {
      disabled: !this.helpers.publicDashboard.projectHasFeatureEnabled()
    }
  }, jsx("div", {
    sel: '.project-name'
  }, "Make Dashboard Public", "\u00A0"), this.helpers.hasWorkspacesFeatureGate() ? jsx("div", {
    sel: '.workspace-name'
  }, "via ".concat(truncate(this.helpers.getCurrentWorkspaceName(), 60))) : null, !this.helpers.publicDashboard.projectHasFeatureEnabled() && jsx(null, null, jsx(SvgIconEl, {
    attrs: {
      icon: "info",
      elref: "disabledInfoIcon"
    }
  }), jsx(TooltipEl, {
    attrs: {
      text: "Share dashboards with others outside your project. \n Project Owners can enable Public Dashboards. \n\n [Enable Public Dashboards](/settings/project/".concat(currentProjectStore.value.id, "/public-dashboards)"),
      placement: Placement.Right,
      elref: "disabledTextTooltip"
    }
  })))), jsx("div", {
    sel: '.controls'
  }, !this.helpers.publicDashboard.canToggleFreePublicDashboards() && !this.helpers.projectHasIntegrated() && jsx(TooltipEl, {
    attrs: {
      text: "Your project has no data! Start sending events today to share this dashboard publicly.",
      placement: Placement.Top,
      elref: "disabledToggleTooltip"
    }
  }), jsx(SwitchEl, {
    sel: '.public-dashboard-switch',
    attrs: {
      on: publicDashboardEnabled,
      disabled: this.helpers.isSharingEnabledForProject() && this.attr("disabled") || !this.helpers.isSharingEnabledForProject() && !this.helpers.publicDashboard.canToggleFreePublicDashboards() || !this.helpers.publicDashboard.projectHasFeatureEnabled(),
      elref: "publicDashboardSwitch"
    },
    on: {
      change: this.helpers.publicDashboard.handlePublicDashboardSwitchChange
    }
  }))) : null)) : null, jsx("footer", {
    sel: '.modal-actions'
  }, jsx("div", {
    sel: '.copy-link-button-wrapper'
  }, jsx(TooltipEl, {
    attrs: {
      text: "Copy the current URL"
    }
  }), jsx(ButtonEl, {
    sel: '.copy-link-button',
    attrs: {
      theme: ButtonTheme.Tertiary,
      icon: this.helpers.shouldShowCopyLinkSpinner() ? "spinner" : "link",
      disabled: this.helpers.shouldShowCopyLinkSpinner()
    },
    on: {
      click: this.helpers.shortUrl.handleCopyLinkClick
    }
  }, "Copy Link"), showCopyLinkText ? jsx("input", {
    sel: '.copy-link-input',
    attrs: {
      type: "text",
      readonly: true
    },
    hook: {
      insert: _ref6 => {
        let {
          elm
        } = _ref6;
        return this.helpers.handleCopyLinkInputInsert(elm);
      }
    },
    on: {
      click: this.helpers.handleCopyLinkInputClick
    }
  }) : null), !showCopyLinkText && !this.helpers.publicDashboard.showPublicDashboardSwitch() && !this.helpers.shouldShowUpsellBanner() ? jsx("span", {
    sel: '.link-info'
  }, "Project members with this link can view") : null, this.helpers.publicDashboard.showPublicDashboardSwitch() && this.helpers.publicDashboard.projectHasFeatureEnabled() ? jsx(ButtonEl, {
    sel: '.public-dashboard-detail-button',
    attrs: {
      elref: "publicDashboardDetailsButton",
      theme: ButtonTheme.Tertiary,
      icon: "settings"
    },
    on: {
      click: () => this.helpers.publicDashboard.updateSharingModalViewType(SharingModalViewType.PublicDashboardDetails)
    }
  }, "Public Dashboard Details") : null)));
}